import { ChevronIcon } from './icons'
import styles from './Pagination.module.css'

interface Props {
  total: number
  limit: number
  offset: number
  onChange: (offset: number) => void
}

/**
 * Постраничная навигация: «1 … 4 5 6 … 20».
 *
 * Показывает край, окно вокруг текущей и многоточия между ними.
 * Все страницы подряд рисовать нельзя: на истории в двести страниц
 * пагинация занимает больше места, чем сама таблица.
 */
export function Pagination({ total, limit, offset, onChange }: Props) {
  const pages = Math.max(1, Math.ceil(total / limit))
  if (pages <= 1) return null

  const current = Math.floor(offset / limit) + 1
  const numbers = window_(current, pages)

  return (
    <nav className={styles.row} aria-label="Страницы">
      <button type="button" className={styles.page} disabled={current === 1}
              onClick={() => onChange((current - 2) * limit)} aria-label="Предыдущая">
        <span style={{ transform: 'rotate(90deg)', display: 'grid' }}><ChevronIcon /></span>
      </button>

      {numbers.map((page, index) =>
        page === null ? (
          <span key={`gap-${index}`} className={styles.gap} aria-hidden="true">...</span>
        ) : (
          <button
            key={page}
            type="button"
            className={page === current ? `${styles.page} ${styles.on}` : styles.page}
            aria-current={page === current ? 'page' : undefined}
            onClick={() => onChange((page - 1) * limit)}
          >
            {page}
          </button>
        ),
      )}

      <button type="button" className={styles.page} disabled={current === pages}
              onClick={() => onChange(current * limit)} aria-label="Следующая">
        <span style={{ transform: 'rotate(-90deg)', display: 'grid' }}><ChevronIcon /></span>
      </button>
    </nav>
  )
}

/** Номера с многоточиями. `null` — место разрыва. */
function window_(current: number, pages: number): (number | null)[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1)

  const around = [current - 1, current, current + 1].filter((p) => p > 1 && p < pages)
  const set = new Set<number>([1, ...around, pages])
  const sorted = [...set].sort((a, b) => a - b)

  const result: (number | null)[] = []
  let previous = 0
  for (const page of sorted) {
    if (previous && page - previous > 1) result.push(null)
    result.push(page)
    previous = page
  }
  return result
}
